import {createContext, ReactNode, useContext, useMemo, useSyncExternalStore} from "react";
import {SessionUser, tokenStore} from "./tokenStore";
import {SiteAdministratorClaim} from "../api/models";

interface AuthState {
    user: SessionUser | null;
    isAdmin: boolean;
    login(username: string, password: string): Promise<void>;
    logout(): Promise<void>;
    renew(): Promise<unknown>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({children}: { children: ReactNode }) {
    // Follows the stored session, including sign-ins and sign-outs in other tabs.
    const accessToken = useSyncExternalStore(tokenStore.subscribe, tokenStore.snapshot);
    const state = useMemo<AuthState>(() => {
        const user = accessToken ? tokenStore.user() : null;
        return {
            user,
            isAdmin: !!user?.claims?.includes(SiteAdministratorClaim),
            login: (username, password) => tokenStore.login(username, password),
            logout: () => tokenStore.logout(),
            renew: () => tokenStore.renew()
        };
    }, [accessToken]);
    return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const state = useContext(AuthContext);
    if (!state) {
        throw new Error('useAuth needs an AuthProvider');
    }
    return state;
}
